import React from "react";
import { Button, Container, Flex, Text } from "theme-ui";
import { BackButton } from "components/BackButton";
import { ActionDrawer } from "components/ActionDrawer";
import { LabelWithBalance } from "components/LabelWithBalance";
import { SummaryTable } from "components/SummaryTable";
import { humanFriendlyNumber } from "utils/number";
import { useRelayer } from "hooks/useRelayer";
import { useRecoilState } from "recoil";
import {
  borrowAmount,
  borrowCurrency,
  borrowLoading,
  borrowTxHash,
} from "../state";
import { PoofAccountGlobal } from "hooks/poofAccount";
import { useBorrow } from "hooks/actions/useBorrow";
import { ProofGenLoading } from "components/Loaders/ProofGenLoading";

interface IProps {
  onBackClick: () => void;
  onConfirmClick: () => void;
}

export const PRECISION = 7;

export const ConfirmBorrow: React.FC<IProps> = ({
  onBackClick,
  onConfirmClick,
}) => {
  const [amount] = useRecoilState(borrowAmount);
  const [currency] = useRecoilState(borrowCurrency);
  const [txHash] = useRecoilState(borrowTxHash);
  const [loading, setLoading] = useRecoilState(borrowLoading);

  const { relayer, relayerFee } = useRelayer(amount, currency);
  const { actWithPoofAccount } = PoofAccountGlobal.useContainer();
  const borrow = useBorrow();

  const finalBorrowAmount = Number(amount) + Number(relayerFee);

  const handleBorrow = async () => {
    actWithPoofAccount(
      async (privateKey) => {
        try {
          await borrow(privateKey);
          onConfirmClick();
          if (!txHash) {
            alert(
              "No response from relayer. Check your account in the explorer or try again"
            );
          }
        } catch (e: any) {
          if (e.response) {
            console.error(e.response.data.error);
          } else {
            console.debug(e);
            alert(e.message);
          }
        } finally {
          setLoading(false);
        }
      },
      () => {}
    );
  };

  return (
    <Container>
      <BackButton onClick={onBackClick} />
      <br />
      <Text sx={{ mb: 1 }} variant="subtitle">
        Transaction Summary
      </Text>
      <br />
      <Text sx={{ mb: 4 }} variant="regularGray">
        Please review the transaction before continuing
      </Text>
      <br />

      <SummaryTable
        title="Summary"
        lineItems={[
          {
            label: "Borrow Amount",
            value: `${humanFriendlyNumber(amount)} ${currency}`,
          },
          {
            label: `Relayer Fee (${relayer?.relayerFee}%) + Gas`,
            value: `${
              relayerFee ? humanFriendlyNumber(relayerFee) : "-"
            } ${currency}`,
          },
        ]}
        totalItem={{
          label: "Total",
          value: `${humanFriendlyNumber(finalBorrowAmount)} ${currency}`,
        }}
      />
      <ActionDrawer>
        {loading ? (
          <Flex sx={{ justifyContent: "flex-end" }}>
            <ProofGenLoading />
          </Flex>
        ) : (
          <Flex sx={{ justifyContent: "space-between" }}>
            <LabelWithBalance
              label="Total"
              amount={finalBorrowAmount}
              currency={currency}
            />
            <Button onClick={handleBorrow}>Borrow</Button>
          </Flex>
        )}
      </ActionDrawer>
    </Container>
  );
};
